/**
 * Serviço centralizado de dados — Re Leve IA (Etapa 3B)
 * Expõe funções limpas para os componentes.
 * Fonte principal para UI: clientesSimulacao.json (valores plausíveis).
 * Fonte raw técnica: clientes.json (disponível apenas para o desenvolvedor).
 */

import clientesRaw from '../data/clientes.json';
import clientesSimRaw from '../data/clientesSimulacao.json';
import cenariosRaw from '../data/cenarios.json';
import guardrailsRaw from '../data/guardrails.json';
import kitsRaw from '../data/kits.json';
import memoriaRaw from '../data/memoria.json';
import promocoesRaw from '../data/promocoes.json';
import qualityReportRaw from '../data/dataQualityReport.json';

import { adaptScenario, adaptGuardrail, adaptKit } from './dataAdapters';
import { normalizeText } from './formatters';

// ── Inicialização ──────────────────────────────────────────────────────────────

const _cenarios = Array.isArray(cenariosRaw) ? cenariosRaw.map(adaptScenario) : [];
const _guardrails = Array.isArray(guardrailsRaw) ? guardrailsRaw.map(adaptGuardrail) : [];
const _kits = Array.isArray(kitsRaw) ? kitsRaw.map(adaptKit) : [];
const _memoria = Array.isArray(memoriaRaw) ? memoriaRaw : [];
const _promocoes = Array.isArray(promocoesRaw) ? promocoesRaw : [];

// Clientes simulados (fonte principal da UI)
const _clientesSim = Array.isArray(clientesSimRaw) ? clientesSimRaw : [];

// Clientes raw (disponível apenas para o painel do desenvolvedor)
const _clientesRaw = Array.isArray(clientesRaw) ? clientesRaw : [];

// ── Funções de clientes simulados (uso da UI) ─────────────────────────────────

export function getSimulationCustomers() {
  return _clientesSim;
}

export function getSimulationCustomerById(clienteId) {
  if (!clienteId) return null;
  return _clientesSim.find(c => c.clienteId === clienteId) || null;
}

export function getSimulationCustomersBySegment(segment) {
  if (!segment || segment === 'Todos') return _clientesSim;
  const norm = normalizeText(segment);
  return _clientesSim.filter(c => normalizeText(c.segmentoComercial || '').includes(norm));
}

export function getSimulationSegments() {
  const counts = {};
  for (const c of _clientesSim) {
    const s = c.segmentoComercial || 'Indefinido';
    counts[s] = (counts[s] || 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([segment, total]) => ({ segment, total }));
}

// ── Funções de clientes raw (apenas para o desenvolvedor) ─────────────────────

export function getRawCustomerById(clienteId) {
  if (!clienteId) return null;
  return _clientesRaw.find(c => c.clienteId === clienteId) || null;
}

/**
 * Retorna o contexto completo para o painel do desenvolvedor:
 * dados simulados + dados raw + memórias + cenários + guardrails + kits.
 */
export function getDeveloperCustomerContext(clienteId) {
  const sim = getSimulationCustomerById(clienteId);
  const raw = getRawCustomerById(clienteId);
  const memorias = getMemoriesByCustomerId(clienteId);
  const cenarios = getScenariosByCustomerId(clienteId);
  const guardrailsIds = cenarios.flatMap(s => s.guardrailsAplicados || []);
  const guardrailsRelev = getGuardrailsByIds([...new Set(guardrailsIds)]);

  // Kits sugeridos pelo segmento
  const segNorm = normalizeText(sim?.segmentoComercial || '');
  const kitsRelev = _kits.filter(k =>
    (k.segmentosAlvo || []).some(s => normalizeText(s).includes(segNorm) || segNorm.includes(normalizeText(s)))
  );

  return {
    simulado: sim,
    raw,
    memorias,
    cenarios,
    guardrailsRelevantes: guardrailsRelev,
    kitsSugeridos: kitsRelev.length > 0 ? kitsRelev : _kits.slice(0, 2),
  };
}

// ── Compatibilidade com código legado (Etapa 3) ───────────────────────────────
// CustomerSelector e outros componentes existentes usam getAllCustomers/getCustomerById.
// Redirecionamos para os dados simulados para manter compatibilidade sem refatorar tudo.

export function getAllCustomers() {
  // Transforma clientesSimulacao no formato que os componentes legados esperam
  return _clientesSim.map(sim => ({
    id: sim.clienteId,
    clienteId: sim.clienteId,
    clienteAnonimo: sim.clienteAnonimo,
    displayName: `${sim.segmentoComercial} · ${sim.clienteId}`,
    name: `${sim.segmentoComercial} · ${sim.clienteId}`,
    segmento: sim.segmentoComercial,
    segment: sim.segmentoComercial,
    // Valores simulados (nunca os valores brutos)
    totalPedidos: sim.totalPedidosSimulado,
    ticketMedio: sim.ticketMedioSimulado,
    ticketMedioFormatado: sim.ticketMedioSimuladoFmt,
    ticketMediano: sim.ticketMedianoSimulado,
    ticketMedianoFormatado: sim.ticketMedianoSimuladoFmt,
    maiorPedido: sim.maiorPedidoSimulado,
    maiorPedidoFormatado: sim.maiorPedidoSimuladoFmt,
    menorPedido: sim.menorPedidoSimulado,
    menorPedidoFormatado: sim.menorPedidoSimuladoFmt,
    valorTotalAcumulado: sim.valorTotalAcumuladoSimulado,
    valorTotalAcumuladoFormatado: sim.valorTotalAcumuladoSimuladoFmt,
    primeiraCompra: sim.primeiraCompraSimulada,
    ultimaCompra: sim.ultimaCompraSimulada,
    diasDesdeUltimaCompra: sim.diasDesdeUltimaCompraSimulado,
    // Perfil qualitativo
    hipotesePerfil: sim.hipoteseDePerfil,
    objetivoProvavel: sim.objetivoProvavel,
    propensaoRecompra: sim.propensaoARecompra,
    propensaoUpsell: sim.propensaoAUpsell,
    propensaoCrossSell: sim.propensaoACrossSell,
    interesseAcompanhamentoCalorico: sim.interesseEmAcompCalorico,
    observacaoParaChatbot: sim.observacaoParaChatbot,
    alertaSimulacao: sim.alertaSimulacao,
    fonteValores: sim.fonteValores,
    nivelConfianca: null,
    fontePrincipal: sim.fonteValores,
    origemDoDado: 'Simulação',
    // Dados raw disponíveis mas não expostos diretamente
    _sim: sim,
  }));
}

export function getCustomerById(clienteId) {
  if (!clienteId) return null;
  const all = getAllCustomers();
  return all.find(c => c.clienteId === clienteId || c.id === clienteId) || null;
}

export function getCustomersBySegment(segment) {
  if (!segment || segment === 'Todos') return getAllCustomers();
  const norm = normalizeText(segment);
  return getAllCustomers().filter(c => normalizeText(c.segmento || '').includes(norm));
}

export function getAvailableSegments() {
  return getSimulationSegments();
}

// ── Cenários ──────────────────────────────────────────────────────────────────

export function getAllScenarios() { return _cenarios; }

export function getScenarioById(id) {
  return _cenarios.find(s => s.cenarioId === id || s.id === id) || null;
}

export function getScenariosByCustomerId(clienteId) {
  return _cenarios.filter(s => s.clienteId === clienteId);
}

// ── Guardrails ────────────────────────────────────────────────────────────────

export function getAllGuardrails() { return _guardrails; }

export function getGuardrailsByIds(ids) {
  if (!ids || ids.length === 0) return [];
  return _guardrails.filter(g => ids.includes(g.id));
}

// ── Kits ──────────────────────────────────────────────────────────────────────

export function getAllKits() { return _kits; }

export function getKitById(id) {
  return _kits.find(k => k.id === id) || null;
}

// ── Memórias ──────────────────────────────────────────────────────────────────

export function getMemoriesByCustomerId(clienteId) {
  if (!clienteId) return [];
  return _memoria.filter(m => m.clienteId === clienteId || m.cliente === clienteId);
}

// ── Promoções ─────────────────────────────────────────────────────────────────

export function getAllPromocoes() { return _promocoes; }

// ── Qualidade de dados ────────────────────────────────────────────────────────

export function getDataQualitySummary() {
  const counts = qualityReportRaw?.counts || {};
  return {
    clientes: counts.clientes ?? _clientesSim.length,
    pedidos: counts.pedidos ?? 0,
    guardrails: counts.guardrails ?? _guardrails.length,
    kits: counts.kits ?? _kits.length,
    memoria: counts.memoria ?? _memoria.length,
    cenarios: counts.cenarios ?? _cenarios.length,
    conhecimento: counts.conhecimento ?? 0,
  };
}
